// File: inquiryTypeCatalog.js
//
// The DEFAULT list of inquiry topics users can pick from on the
// Inquiry page (e.g. "Housing Stability", "Credit Health", etc.).
//
// IMPORTANT: this file is only used to SEED the database the first
// time someone opens the Inquiry page on a fresh project. After that,
// the live list lives in Firestore (`inquiryTypes` collection) and
// admins edit it from the Admin portal's Management tab.
// In other words: editing this file will NOT change the topics that
// users see in production. Edit the inquiry types in the Admin
// portal instead.
//
// What each field on a type means:
//   key                   - short id, also used as the Firestore doc id
//   label                 - the name shown to users in the dropdown
//   description           - short helper text shown under the label
//   sortOrder             - lower numbers show higher in the list
//   active                - if false, the topic is hidden from users
//   autoOpenThread        - if true, submitting this kind of inquiry
//                           also opens a message thread automatically
//   defaultThreadMessage  - the opening message posted in that thread
//   followUpQuestions     - reference list of follow-up Qs admins
//                           may ask the user (informational)

// Turns a label like "Housing Stability & Urgency" into a clean
// machine-friendly key like "housing_stability_and_urgency" so it
// can be used as a Firestore document id.
export function slugifyInquiryTypeKey(value = "") {
  return String(value)
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_");
}

export const DEFAULT_INQUIRY_TYPES = [
  {
    key: "housing_stability_urgency",
    label: "Housing Stability & Urgency",
    description:
      "For eviction, foreclosure, being behind on rent or mortgage, utility shutoff, or immediate housing instability.",
    sortOrder: 1,
    active: true,
    autoOpenThread: true,
    defaultThreadMessage: `Thank you for submitting your inquiry. To help us understand your housing situation faster, please reply with any of the following that apply:

- Whether you received an eviction or foreclosure notice
- What type of notice it was
- When you received it
- Whether it lists a deadline or court date
- How many months behind you are on rent or mortgage
- Whether you currently have a safe place to stay`,
    followUpQuestions: [
      "Have you received an eviction or foreclosure notice?",
      "What type of notice did you receive?",
      "When did you receive the notice?",
      "Does the notice list a deadline or court date?",
      "How many months are you behind on rent or mortgage?",
      "Do you have a safe place to live right now?",
    ],
  },
  {
    key: "housing_goals_readiness",
    label: "Housing Goals & Readiness",
    description:
      "For renting, homebuying readiness, mortgage pre-approval, savings, and credit readiness for housing goals.",
    sortOrder: 2,
    active: true,
    autoOpenThread: true,
    defaultThreadMessage: `Thank you for reaching out. To help us understand your housing goals, please reply with any of the following that apply:

- Whether you are renting, owning, or planning to buy
- When you hope to purchase a home
- Whether you checked your credit in the last 6 months
- Whether you have been pre-approved for a mortgage
- Anything currently making you feel unready to move forward`,
    followUpQuestions: [
      "Are you renting, owning, or planning to buy?",
      "When do you plan to purchase a home?",
      "Have you checked your credit score in the past 6 months?",
      "Have you been pre-approved for a mortgage?",
    ],
  },
  {
    key: "debt_cash_flow_stresses",
    label: "Debt & Cash Flow Stresses",
    description:
      "For multiple debts, minimum payment struggles, collections, lawsuits, or urgent financial pressure.",
    sortOrder: 3,
    active: true,
    autoOpenThread: true,
    defaultThreadMessage: `Thank you for submitting this inquiry. To help us understand your financial situation, please reply with any of the following that apply:

- What types of debt you currently have
- Whether you are able to make minimum payments
- Whether any accounts are in collections or legal action
- What feels most stressful financially right now
- Whether a specific deadline or bill is causing the most pressure`,
    followUpQuestions: [
      "What types of debt do you have?",
      "Are you able to make minimum payments?",
      "Are any accounts in collections or legal action?",
      "What feels most stressful financially right now?",
    ],
  },
  {
    key: "credit_health_goals",
    label: "Credit Health & Goals",
    description:
      "For credit score issues, report errors, disputes, and credit barriers tied to housing, loans, jobs, or applications.",
    sortOrder: 4,
    active: true,
    autoOpenThread: true,
    defaultThreadMessage: `Thanks for reaching out. To help us assess your credit-related needs, please reply with any of the following that apply:

- Your current credit score or score range, if you know it
- Whether you believe there are errors on your credit report
- Whether you are applying for housing, credit, or employment soon
- Whether you were already denied because of credit
- Whether improving your score is needed for your next step`,
    followUpQuestions: [
      "Do you know your current credit score or credit score range?",
      "Are there errors in your credit report?",
      "Are you applying for housing, credit, or employment?",
    ],
  },
  {
    key: "student_loan_status",
    label: "Student Loan Status",
    description:
      "For federal or private student loans, delinquency, default, forgiveness, IDR questions, and payment relief.",
    sortOrder: 5,
    active: true,
    autoOpenThread: true,
    defaultThreadMessage: `Thank you for your inquiry. To help us understand your student loan situation, please reply with any of the following that apply:

- Whether your loans are federal or private
- Whether your loans are current, delinquent, or in default
- Your current monthly payment, if known
- Whether collections, garnishment, or servicer notices are involved
- What outcome you are hoping for, such as lower payments, forgiveness, or getting back on track`,
    followUpQuestions: [
      "Do you have federal or private student loans?",
      "Are your loans current, delinquent, or defaulted?",
      "What is your current monthly student loan payment?",
      "Are wages, tax refunds, or benefits being garnished?",
      "What outcome are you seeking?",
    ],
  },
  {
    key: "bankruptcy_screening",
    label: "Bankruptcy Screening",
    description:
      "For people exploring bankruptcy, facing lawsuits, garnishments, levies, or serious creditor legal action.",
    sortOrder: 6,
    active: true,
    autoOpenThread: true,
    defaultThreadMessage: `Thank you for reaching out. To help us understand whether bankruptcy-related support may fit your situation, please reply with any of the following that apply:

- Whether you are considering bankruptcy
- What debts or pressures are making you consider it
- Whether you have been sued or had wages garnished
- Whether creditors are taking legal action
- Whether there are any upcoming court dates, levies, or urgent deadlines`,
    followUpQuestions: [
      "Are you considering bankruptcy?",
      "Have you been sued or had wages garnished?",
      "Are creditors taking legal action?",
    ],
  },
  {
    key: "business_ownership_income",
    label: "Business Ownership & Income",
    description:
      "For startups, existing businesses, business growth, mixed personal/business finances, and funding readiness.",
    sortOrder: 7,
    active: true,
    autoOpenThread: true,
    defaultThreadMessage: `Thank you for submitting your inquiry. To help us understand your business situation, please reply with any of the following that apply:

- Whether you are starting or operating a business
- How long you have been in business
- Whether the business is currently generating income
- Whether personal and business finances are mixed
- Whether you are seeking funding or capital`,
    followUpQuestions: [
      "Are you starting or operating a business?",
      "How long have you been in business?",
      "Are personal and business finances mixed?",
      "Are you seeking funding?",
    ],
  },
  {
    key: "asset_protection_risk",
    label: "Asset Protection & Risk",
    description:
      "For protecting savings or assets, medical bill risk, lawsuit exposure, and life insurance coverage concerns.",
    sortOrder: 8,
    active: true,
    autoOpenThread: true,
    defaultThreadMessage: `Thank you for reaching out. To help us understand your asset protection concerns, please reply with any of the following that apply:

- Whether you have savings or assets you want to protect
- What kinds of assets are most important to protect
- Whether medical bills or legal risks are a concern
- Whether unpaid bills, collections, or lawsuits could put assets at risk
- Whether you currently have life insurance coverage and up-to-date beneficiaries`,
    followUpQuestions: [
      "Do you have savings or assets you want to protect?",
      "Are you worried about medical bills or lawsuits?",
      "Do you have adequate life insurance?",
    ],
  },
  {
    key: "retirement_planning",
    label: "Retirement Planning",
    description:
      "For retirement savings, readiness, near-retirement planning, and understanding retirement income sources.",
    sortOrder: 9,
    active: true,
    autoOpenThread: true,
    defaultThreadMessage: `Thank you for your inquiry. To help us understand your retirement planning needs, please reply with any of the following that apply:

- Whether you are currently saving for retirement
- Whether you feel on track for retirement
- What retirement accounts or income sources you expect to rely on
- Whether debt or expenses are making it hard to save
- Whether you feel unprepared for a retirement timeline that is getting closer`,
    followUpQuestions: [
      "Are you currently saving for retirement?",
      "Do you feel on track for retirement?",
      "Do you know your expected income sources?",
    ],
  },
  {
    key: "legacy_planning",
    label: "Legacy Planning",
    description:
      "For wills, beneficiaries, dependents, future planning concerns, attorney readiness, and making plans easier for family.",
    sortOrder: 10,
    active: true,
    autoOpenThread: true,
    defaultThreadMessage: `Thank you for reaching out. To help us understand your legacy planning needs, please reply with any of the following that apply:

- Whether you already have a will or beneficiaries in place
- Whether anyone depends on you financially
- What planning concerns feel most important right now
- Whether you want guidance before meeting with an attorney
- Whether there is a life event, deadline, or family concern prompting this now`,
    followUpQuestions: [
      "Do you have a will or beneficiaries?",
      "Are there dependents relying on you?",
      "What planning concerns do you have?",
    ],
  },
];